import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpSearch } from "./HelpSearch";
import { HelpToc, HelpTocMobile } from "./HelpToc";
import { HelpSection } from "./HelpSection";
import { HelpEmpty } from "./HelpEmpty";
import { HelpActions } from "./HelpActions";
import { helpSections } from "./helpContent";

const normalise = (value: string) => value.toLowerCase().trim();

export const HelpCenter = () => {
	const { t, i18n } = useTranslation();
	const [query, setQuery] = useState("");
	const [expandedTopics, setExpandedTopics] = useState<string[]>([]);
	const [activeSection, setActiveSection] = useState<string>(helpSections[0]?.id ?? "");
	const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

	const searchIndex = useMemo(() => {
		return helpSections.flatMap((section) => {
			const sectionTitle = t(section.titleKey);
			return section.topics.map((topic) => {
				const title = t(topic.titleKey);
				const overview = t(topic.overviewKey);
				const steps = topic.stepsKey
					? (t(topic.stepsKey, { returnObjects: true, defaultValue: [] }) as unknown as string[])
					: [];
				const tips = topic.tipsKey
					? (t(topic.tipsKey, { returnObjects: true, defaultValue: [] }) as unknown as string[])
					: [];
				const haystack = [sectionTitle, title, overview, ...(Array.isArray(steps) ? steps : []), ...(Array.isArray(tips) ? tips : [])]
					.join(" ")
					.toLowerCase();
				return { sectionId: section.id, topicId: topic.id, haystack };
			});
		});
	}, [t, i18n.language]);

	const matchingTopicIds = useMemo(() => {
		const q = normalise(query);
		if (!q) return null;
		const set = new Set<string>();
		for (const entry of searchIndex) {
			if (entry.haystack.includes(q))
				set.add(`${entry.sectionId}.${entry.topicId}`);
		}
		return set;
	}, [query, searchIndex]);

	useEffect(() => {
		if (matchingTopicIds)
			setExpandedTopics(Array.from(matchingTopicIds));
	}, [matchingTopicIds]);

	const findScrollRoot = (): HTMLElement | null => {
		for (const id of Object.keys(sectionRefs.current)) {
			const el = sectionRefs.current[id];
			if (el)
				return el.closest("main");
		}
		return null;
	};

	const scrollToElement = (el: HTMLElement | null) => {
		if (!el) return;
		const root = el.closest("main") as HTMLElement | null;
		if (root) {
			const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - 16;
			root.scrollTo({ top, behavior: "smooth" });
		} else {
			el.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	useEffect(() => {
		if (matchingTopicIds)
			return;
		const root = findScrollRoot();
		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((entry) => entry.isIntersecting)
					.sort((a, b) => (a.target as HTMLElement).offsetTop - (b.target as HTMLElement).offsetTop);
				if (visible.length > 0) {
					const id = (visible[0].target as HTMLElement).dataset.sectionId;
					if (id)
						setActiveSection(id);
				}
			},
			{ root, rootMargin: "0px 0px -70% 0px", threshold: 0 },
		);
		for (const section of helpSections) {
			const el = sectionRefs.current[section.id];
			if (el)
				observer.observe(el);
		}
		return () => observer.disconnect();
	}, [matchingTopicIds]);

	useEffect(() => {
		const applyHash = () => {
			const hash = window.location.hash.replace(/^#/, "");
			if (!hash) return;
			const [sectionId, topicId] = hash.split(".");
			if (topicId) {
				const topicKey = `${sectionId}.${topicId}`;
				setExpandedTopics((prev) => (prev.includes(topicKey) ? prev : [...prev, topicKey]));
				setTimeout(() => {
					scrollToElement(document.getElementById(`help-${topicKey}`));
				}, 80);
			} else {
				setTimeout(() => {
					scrollToElement(document.getElementById(`help-${sectionId}`));
				}, 80);
			}
			setActiveSection(sectionId);
		};
		applyHash();
		window.addEventListener("hashchange", applyHash);
		return () => window.removeEventListener("hashchange", applyHash);
	}, []);

	const handleTocSelect = (sectionId: string) => {
		setActiveSection(sectionId);
		window.history.replaceState(null, "", `#${sectionId}`);
		scrollToElement(document.getElementById(`help-${sectionId}`));
	};

	const sectionRefCallback = (sectionId: string) => (el: HTMLElement | null) => {
		sectionRefs.current[sectionId] = el;
	};

	const isSearching = !!matchingTopicIds;
	const hasAnyMatch = !isSearching || (matchingTopicIds?.size ?? 0) > 0;

	return (
		<div className="max-w-7xl mx-auto">
			<header className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
				<div className="max-w-2xl">
					<h1 className="text-4xl font-bold tracking-tight">{t("help.pageTitle")}</h1>
					<p className="text-muted-foreground mt-3 text-base">{t("help.pageDescription")}</p>
				</div>
				<div className="md:pt-1">
					<HelpActions />
				</div>
			</header>

			<div className="mb-10 max-w-3xl">
				<HelpSearch value={query} onChange={setQuery} />
			</div>

			{!isSearching && (
				<HelpTocMobile
					sections={helpSections}
					activeSection={activeSection}
					onSelect={handleTocSelect}
				/>
			)}

			<div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px] gap-12">
				<div className="min-w-0">
					{!hasAnyMatch ? (
						<HelpEmpty />
					) : (
						<div className="space-y-14">
							{helpSections.map((section) => (
								<div key={section.id} ref={sectionRefCallback(section.id)} data-section-id={section.id}>
									<HelpSection
										section={section}
										expandedTopics={expandedTopics}
										onExpandedChange={setExpandedTopics}
										visibleTopicIds={matchingTopicIds ?? undefined}
									/>
								</div>
							))}
						</div>
					)}
				</div>

				{!isSearching && (
					<HelpToc
						sections={helpSections}
						activeSection={activeSection}
						onSelect={handleTocSelect}
					/>
				)}
			</div>
		</div>
	);
};
